import { useCallback, useEffect, useRef, useState } from "react";
import ProctoringReviewApi from "src/services/apis/proctoringReview";

/*
 * The room's timeline (GET /api/reporting/v1/proctoring-review), fetched the
 * first time the room opens and kept by the report page (V2) for every later
 * open. It never touches report-data.
 */

/**
 * The timeline out of the response, whether it is wrapped in `data` or spread
 * into the body; null when the response holds none.
 * The fields the room reads are the typedefs in rules/fromTimeline.js.
 * @param {Object} res
 * @returns {import("../rules/fromTimeline").ProctoringTimeline|null}
 */
export function timelineOf(res) {
  if (!res || res.success === false) return null;
  const looksLike = (x) => !!x && typeof x === "object" && !Array.isArray(x) && (x.clock || x.questions || x.captures || x.presence);
  const body = looksLike(res.data) ? res.data : looksLike(res.timeline) ? res.timeline : looksLike(res) ? res : null;
  if (!body) return null;
  const length = Number(body.clock && body.clock.length);
  return {
    ...body,
    clock: { origin: null, offsetMs: null, offsetSource: null, ...(body.clock || {}), length: Number.isFinite(length) && length > 0 ? length : 0 },
    presence: body.presence || {},
    parts: Array.isArray(body.parts) ? body.parts : [],
    questions: Array.isArray(body.questions) ? body.questions : [],
    captures: Array.isArray(body.captures) ? body.captures : [],
    references: Array.isArray(body.references) ? body.references : [],
    profiles: Array.isArray(body.profiles) ? body.profiles : [],
    screens: Array.isArray(body.screens) ? body.screens : [],
    integrity: Array.isArray(body.integrity) ? body.integrity : [],
    audio: body.audio || null,
    recordings: body.recordings || {},
  };
}

/**
 * @param {Object} args
 * @param {string} args.linkId
 * @param {string} args.userId
 * @param {boolean} args.open  the room is open: the first open fetches
 * @returns {{status: "idle"|"loading"|"ready"|"error", timeline: Object|null, error: string|null, retry: Function}}
 */
export default function useProctoringTimeline({ linkId, userId, open }) {
  const [state, setState] = useState({ status: "idle", timeline: null, error: null });
  const requested = useRef(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const res = await ProctoringReviewApi.getTimeline(linkId, userId);
      const timeline = timelineOf(res);
      if (timeline) setState({ status: "ready", timeline, error: null });
      else setState({ status: "error", timeline: null, error: (res && typeof res.message === "string" && res.message) || "The proctoring timeline could not be loaded." });
    } catch (err) {
      setState({ status: "error", timeline: null, error: "The proctoring timeline could not be loaded." });
    }
  }, [linkId, userId]);

  useEffect(() => {
    if (!open || requested.current || !linkId || !userId) return;
    requested.current = true;
    load();
  }, [open, linkId, userId, load]);

  return { ...state, retry: load };
}
